import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Notice, Empty, inputCls } from "@/dashboard/ui";
import { Globe, Search, FileEdit, FileText, Link2, Check, Sparkles, AlertTriangle, ExternalLink } from "lucide-react";

const SUBTABS = [
  { id: "audit", label: "Website Audit" },
  { id: "keywords", label: "Keyword Research" },
  { id: "onpage", label: "On-Page SEO" },
  { id: "content", label: "SEO Content" },
  { id: "google", label: "Google Connections" },
];
const SEV_TONE = { high: "red", medium: "amber", low: "grey" };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-neutral-600">{label}</span>
      {children}
    </label>
  );
}

function AuditTab() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [audits, setAudits] = useState(null);
  const [current, setCurrent] = useState(null);

  const load = () => api.get("/seo/audits").then((r) => {
    setAudits(r.data.audits);
    if (r.data.audits.length && !current) setCurrent(r.data.audits[0]);
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runAudit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/audits", { url });
      setCurrent(data);
      toast.success(`Audit complete — ${data.pages.length} page(s) checked, ${data.recommendations.length} recommendations`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (recId) => {
    try {
      const { data } = await api.post(`/seo/audits/${current.id}/recommendations/${recId}/toggle`);
      setCurrent((c) => ({
        ...c,
        recommendations: c.recommendations.map((r) => (r.id === recId ? { ...r, done: data.done } : r)),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const done = current ? current.recommendations.filter((r) => r.done).length : 0;

  return (
    <div className="space-y-5" data-testid="seo-audit-tab">
      <Card className="p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Website SEO audit</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <Field label="Website address">
              <input className={inputCls} value={url} data-testid="seo-audit-url" placeholder="https://yourbusiness.co.uk" onChange={(e) => setUrl(e.target.value)} />
            </Field>
          </div>
          <Btn data-testid="seo-audit-run" disabled={busy || !url.trim()} onClick={runAudit}>
            <Globe size={14} className="mr-1.5 inline" /> {busy ? "Auditing…" : "Run audit"}
          </Btn>
        </div>
        <p className="mt-2 text-[11px] text-neutral-400">Checks live pages for titles, descriptions, headings, canonicals, image alt text, robots.txt, sitemap and broken internal links. Only public websites — private addresses are blocked. Findings are verified, never estimated.</p>
      </Card>

      {current && (
        <>
          <Card className="p-6" data-testid="seo-audit-summary">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#111111]" data-testid="seo-audit-current-url">{current.url}</p>
                <p className="text-xs text-neutral-500">
                  {current.pages.length} page(s) crawled · robots.txt {current.robots_txt ? "found" : "missing"} · sitemap {current.sitemap?.ok ? "found" : "missing"} · {current.broken_links.length} broken link(s)
                </p>
              </div>
              <Pill tone={done === current.recommendations.length && current.recommendations.length ? "green" : "amber"}>
                <span data-testid="seo-audit-progress">{done} of {current.recommendations.length} recommendations done</span>
              </Pill>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div className="h-full rounded-full bg-[#111111] transition-all" style={{ width: `${current.recommendations.length ? (done / current.recommendations.length) * 100 : 0}%` }} />
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs" data-testid="seo-audit-pages">
                <thead>
                  <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-wider text-neutral-400">
                    <th className="py-1.5 pr-3">Page</th><th className="pr-3">Title</th><th className="pr-3">Description</th><th className="pr-3">H1</th><th className="pr-3">Canonical</th><th>Alt text</th>
                  </tr>
                </thead>
                <tbody>
                  {current.pages.map((p) => (
                    <tr key={p.url} className="border-b border-neutral-100">
                      <td className="max-w-52 truncate py-1.5 pr-3 font-medium text-neutral-700">{p.url.replace(current.url.replace(/\/$/, ""), "") || "/"}</td>
                      <td className="pr-3">{p.title ? <Check size={13} className="text-green-600" /> : <AlertTriangle size={13} className="text-red-500" />}</td>
                      <td className="pr-3">{p.meta_description ? <Check size={13} className="text-green-600" /> : <AlertTriangle size={13} className="text-red-500" />}</td>
                      <td className="pr-3">{p.h1_count === 1 ? <Check size={13} className="text-green-600" /> : <span className="text-amber-600">{p.h1_count}</span>}</td>
                      <td className="pr-3">{p.canonical ? <Check size={13} className="text-green-600" /> : <span className="text-neutral-300">—</span>}</td>
                      <td>{p.images ? `${p.images - p.images_missing_alt}/${p.images}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-6" data-testid="seo-audit-recs">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Prioritised recommendations</p>
            <div className="mt-3 space-y-2">
              {[...current.recommendations].sort((a, b) => ({ high: 0, medium: 1, low: 2 }[a.severity] - { high: 0, medium: 1, low: 2 }[b.severity])).map((r) => (
                <label key={r.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 ${r.done ? "border-green-200 bg-green-50/50" : "border-neutral-200"}`} data-testid={`seo-rec-${r.id}`}>
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-neutral-900" checked={!!r.done} onChange={() => toggle(r.id)} data-testid={`seo-rec-toggle-${r.id}`} />
                  <span className="flex-1">
                    <span className={`text-xs ${r.done ? "text-neutral-400 line-through" : "text-neutral-700"}`}>{r.text}</span>
                  </span>
                  <Pill tone={SEV_TONE[r.severity]}>{r.severity}</Pill>
                </label>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-neutral-400">{current.data_note}</p>
          </Card>
        </>
      )}

      {(audits || []).length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="seo-audit-history">
          {audits.map((a) => (
            <button key={a.id} onClick={() => setCurrent(a)} data-testid={`seo-audit-open-${a.id}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${current?.id === a.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
              {a.url.replace(/^https?:\/\//, "").slice(0, 30)} · {String(a.created_at).slice(0, 10)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KeywordsTab() {
  const [form, setForm] = useState({ industry: "", services: "", location: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/marketing/profile").then((r) => {
      const p = r.data.profile || {};
      setForm({ industry: p.industry || "", services: p.services || "", location: p.location || "" });
    }).catch(() => {});
    api.get("/seo/keywords").then((r) => setHistory(r.data.research)).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/keywords", form);
      setResult(data);
      toast.success("Keyword themes ready");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="seo-keywords-tab">
      <Card className="p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI keyword & local keyword ideas</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field label="Industry *"><input className={inputCls} value={form.industry} data-testid="seo-kw-industry" onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} /></Field>
          <Field label="Services"><input className={inputCls} value={form.services} data-testid="seo-kw-services" onChange={(e) => setForm((f) => ({ ...f, services: e.target.value }))} /></Field>
          <Field label="Location"><input className={inputCls} value={form.location} data-testid="seo-kw-location" placeholder="e.g. Leeds" onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></Field>
        </div>
        <Btn className="mt-3" data-testid="seo-kw-generate" disabled={busy || !form.industry.trim()} onClick={generate}>
          <Sparkles size={14} className="mr-1.5 inline" /> {busy ? "Researching…" : "Suggest keywords"}
        </Btn>
        <p className="mt-2 text-[11px] text-neutral-400">AI suggestions grouped by theme and search intent. Search volumes and difficulty are not shown — no keyword data source is connected, so numbers would be invented.</p>
      </Card>

      {result && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="seo-kw-results">
          {result.themes.map((t, i) => (
            <Card key={i} className="p-5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-[#111111]">{t.theme}</p>
                <Pill tone="outline">{t.intent}</Pill>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(t.keywords || []).map((k) => <Pill key={k} tone="grey">{k}</Pill>)}
              </div>
              {(t.long_tail || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Long-tail</p>
                  <ul className="mt-1 list-inside list-disc text-xs text-neutral-600">{t.long_tail.map((k) => <li key={k}>{k}</li>)}</ul>
                </div>
              )}
              {(t.content_ideas || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Content ideas</p>
                  <ul className="mt-1 list-inside list-disc text-xs text-neutral-600">{t.content_ideas.map((k) => <li key={k}>{k}</li>)}</ul>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="seo-kw-history">
          {history.map((h) => (
            <button key={h.id} onClick={() => setResult(h)} data-testid={`seo-kw-open-${h.id}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${result?.id === h.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
              {h.industry}{h.location ? ` · ${h.location}` : ""} · {String(h.created_at).slice(0, 10)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OnpageTab() {
  const [audits, setAudits] = useState([]);
  const [auditId, setAuditId] = useState("");
  const [docs, setDocs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/seo/audits").then((r) => {
      setAudits(r.data.audits);
      if (r.data.audits.length) setAuditId(r.data.audits[0].id);
    }).catch(() => {});
    api.get("/seo/onpage").then((r) => {
      setDocs(r.data.onpage);
      if (r.data.onpage.length) setCurrent(r.data.onpage[0]);
    }).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/onpage", { audit_id: auditId });
      setCurrent(data);
      toast.success("On-page recommendations ready — edit anything before using it");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setPage = (i, k, v) => setCurrent((c) => ({ ...c, pages: c.pages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));

  const save = async () => {
    try {
      await api.put(`/seo/onpage/${current.id}`, { pages: current.pages });
      toast.success("Saved your edits");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="seo-onpage-tab">
      <Card className="p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI on-page recommendations</p>
        {audits.length === 0 ? (
          <p className="mt-3 text-sm text-neutral-600">Run a website audit first — recommendations are written from your verified findings.</p>
        ) : (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Field label="Based on audit">
              <select className={inputCls} value={auditId} data-testid="seo-onpage-audit" onChange={(e) => setAuditId(e.target.value)}>
                {audits.map((a) => <option key={a.id} value={a.id}>{a.url} · {String(a.created_at).slice(0, 10)}</option>)}
              </select>
            </Field>
            <Btn data-testid="seo-onpage-generate" disabled={busy || !auditId} onClick={generate}>
              <Sparkles size={14} className="mr-1.5 inline" /> {busy ? "Writing…" : "Generate recommendations"}
            </Btn>
          </div>
        )}
        <p className="mt-2 text-[11px] text-neutral-400">Improved titles, descriptions, headings and content ideas you can copy or edit. Nothing is changed on your website automatically.</p>
      </Card>

      {current && (
        <div className="space-y-4" data-testid="seo-onpage-results">
          {current.pages.map((p, i) => (
            <Card key={i} className="p-5">
              <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-neutral-500"><ExternalLink size={12} /> {p.url}</p>
              <div className="space-y-2.5">
                <Field label={`Title (${(p.title || "").length}/60)`}>
                  <input className={inputCls} value={p.title || ""} data-testid={`seo-onpage-title-${i}`} onChange={(e) => setPage(i, "title", e.target.value)} />
                </Field>
                <Field label={`Meta description (${(p.meta_description || "").length}/160)`}>
                  <textarea rows={2} className={inputCls} value={p.meta_description || ""} data-testid={`seo-onpage-desc-${i}`} onChange={(e) => setPage(i, "meta_description", e.target.value)} />
                </Field>
                <Field label="H1 heading">
                  <input className={inputCls} value={p.h1 || ""} data-testid={`seo-onpage-h1-${i}`} onChange={(e) => setPage(i, "h1", e.target.value)} />
                </Field>
                {(p.content_ideas || []).length > 0 && (
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Content ideas</p>
                    <ul className="mt-1 list-inside list-disc text-xs text-neutral-600">{p.content_ideas.map((x, j) => <li key={j}>{x}</li>)}</ul></div>
                )}
                {(p.faqs || []).length > 0 && (
                  <div><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Suggested FAQs</p>
                    <ul className="mt-1 list-inside list-disc text-xs text-neutral-600">{p.faqs.map((x, j) => <li key={j}>{x}</li>)}</ul></div>
                )}
              </div>
            </Card>
          ))}
          <div className="flex justify-end">
            <Btn data-testid="seo-onpage-save" onClick={save}>Save edits</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

const CONTENT_KINDS = [
  { id: "blog", label: "Blog post" },
  { id: "faq", label: "FAQ section" },
  { id: "service", label: "Service page" },
];

function ContentTab() {
  const [form, setForm] = useState({ kind: "blog", topic: "", keywords: "", location: "" });
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/seo/content").then((r) => setDocs(r.data.content)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/seo/content", form);
      toast.success("Draft written — review it before using anything");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/seo/content/${editing.id}`, { content: editing.content, topic: editing.topic });
      toast.success("Draft saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="seo-content-tab">
      <Card className="p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI SEO content drafts</p>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="seo-content-kinds">
          {CONTENT_KINDS.map((k) => (
            <button key={k.id} data-testid={`seo-content-kind-${k.id}`} onClick={() => setForm((f) => ({ ...f, kind: k.id }))}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${form.kind === k.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
              {k.label}
            </button>
          ))}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field label="Topic *"><input className={inputCls} value={form.topic} data-testid="seo-content-topic" placeholder="e.g. balayage aftercare" onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))} /></Field>
          <Field label="Keywords"><input className={inputCls} value={form.keywords} data-testid="seo-content-keywords" onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} /></Field>
          <Field label="Location"><input className={inputCls} value={form.location} data-testid="seo-content-location" onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></Field>
        </div>
        <Btn className="mt-3" data-testid="seo-content-generate" disabled={busy || !form.topic.trim()} onClick={generate}>
          <Sparkles size={14} className="mr-1.5 inline" /> {busy ? "Writing…" : "Write draft"}
        </Btn>
        <p className="mt-2 text-[11px] text-neutral-400">Drafts never invent statistics, reviews or claims, and nothing is published — review and edit first.</p>
      </Card>

      {docs === null ? null : docs.length === 0 ? (
        <Empty testid="seo-content-empty" icon={FileText} title="No SEO content yet" body="Generate a blog, FAQ or service-page draft above." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="seo-content-list">
          {docs.map((d) => (
            <Card key={d.id} className="p-5" data-testid={`seo-content-card-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-[#111111]">{d.topic}</p>
                <Pill tone="outline">{CONTENT_KINDS.find((k) => k.id === d.kind)?.label || d.kind}</Pill>
              </div>
              <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs text-neutral-600">{d.content}</p>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-[11px] text-neutral-400">Updated {String(d.updated_at).slice(0, 10)}</p>
                <Btn variant="ghost" data-testid={`seo-content-edit-${d.id}`} onClick={() => setEditing({ ...d })}>
                  <FileEdit size={13} className="mr-1 inline" /> Edit
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={`Edit ${CONTENT_KINDS.find((k) => k.id === editing.kind)?.label || "draft"}`} onClose={() => setEditing(null)} testid="seo-content-edit-modal">
          <div className="space-y-3">
            <Field label="Topic"><input className={inputCls} value={editing.topic} data-testid="seo-content-edit-topic" onChange={(e) => setEditing((c) => ({ ...c, topic: e.target.value }))} /></Field>
            <Field label="Content">
              <textarea rows={14} className={inputCls} value={editing.content} data-testid="seo-content-edit-body" onChange={(e) => setEditing((c) => ({ ...c, content: e.target.value }))} />
            </Field>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn data-testid="seo-content-edit-save" onClick={saveEdit}>Save draft</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function GoogleTab() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/seo/google/status").then((r) => setStatus(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!status) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;

  const CardFor = ({ title, desc, state, testid }) => (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-[#111111]">{title}</p>
        <Pill tone={state === "connected" ? "green" : state === "ready_to_connect" ? "amber" : "grey"} data-testid={`${testid}-state`}>
          {state === "connected" ? "Connected" : state === "ready_to_connect" ? "Ready to connect" : "Needs setup"}
        </Pill>
      </div>
      <p className="mt-2 text-xs text-neutral-500">{desc}</p>
      <Btn className="mt-3" variant="ghost" disabled={state !== "ready_to_connect"} data-testid={`${testid}-connect`}
        title={state === "needs_setup" ? "Google OAuth credentials must be configured by your platform administrator first" : ""}>
        <Link2 size={13} className="mr-1 inline" /> {state === "connected" ? "Reconnect" : "Connect"}
      </Btn>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="seo-google-tab">
      <Notice testid="seo-google-note" title="Google connections are prepared, not yet active"
        body="Search Console metrics (clicks, impressions, CTR, position) and Business Profile details appear here only after an official Google OAuth connection. Nothing is estimated or simulated." />
      <div className="grid gap-4 sm:grid-cols-2">
        <CardFor title="Google Search Console" testid="seo-gsc-card" state={status.search_console.state}
          desc="Real clicks, impressions, CTR, average position and top queries for your verified properties, with date filters and comparisons." />
        <CardFor title="Google Business Profile" testid="seo-gbp-card" state={status.business_profile.state}
          desc="Your business locations, profile details and optimisation suggestions via the official Google APIs." />
      </div>
      <Card className="p-5" data-testid="seo-google-setup">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Setup required before connecting</p>
        <ol className="mt-2 list-inside list-decimal space-y-1 text-xs text-neutral-600">
          {status.instructions.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
        <p className="mt-2 text-[11px] text-neutral-400">{status.data_note}</p>
      </Card>
    </div>
  );
}

export default function SEOPanel() {
  const [tab, setTab] = useState("audit");
  return (
    <div className="space-y-5" data-testid="seo-panel">
      <div className="flex flex-wrap gap-1.5 rounded-2xl bg-neutral-100 p-1.5" role="tablist" data-testid="seo-subtabs">
        {SUBTABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`seo-subtab-${t.id}`} onClick={() => setTab(t.id)}
            className={`rounded-xl px-3.5 py-2 text-xs font-semibold transition-all ${tab === t.id ? "bg-white text-[#111111] shadow-sm" : "text-neutral-500 hover:text-[#111111]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "audit" && <AuditTab />}
      {tab === "keywords" && <KeywordsTab />}
      {tab === "onpage" && <OnpageTab />}
      {tab === "content" && <ContentTab />}
      {tab === "google" && <GoogleTab />}
    </div>
  );
}
